/**
 * Shared number formatting. `null` always renders as "Unknown" — never as 0
 * or a blank — because a null quantity or price is an absent fact, not a
 * zero one. (Stock and list price have their own wording for null, in
 * lib/labels.ts: "Stock unknown", "Contact ESL for pricing".)
 */
const UNKNOWN = 'Unknown'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const quantityFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

export function formatCurrency(value: number | null): string {
  return value === null ? UNKNOWN : currencyFormatter.format(value)
}

export function formatQuantity(value: number | null): string {
  return value === null ? UNKNOWN : quantityFormatter.format(value)
}

/**
 * `value` is a `date` column — a plain 'YYYY-MM-DD' string with no time
 * component. Parsed and formatted in UTC so the calendar date shown never
 * shifts a day for a viewer west of UTC, which `new Date('YYYY-MM-DD')` + a
 * local-zone formatter would do. Null means missing data ("Unknown"), same
 * as every other formatter above — for orders.order_date, invoices.
 * invoice_date, etc. Line dates (ESL promise / Updated / Date pending) are
 * worded by lib/order-display.ts, which formats the date itself with this.
 */
export function formatDate(value: string | null): string {
  if (value === null) return UNKNOWN
  return dateFormatter.format(new Date(`${value}T00:00:00Z`))
}

/**
 * A unit price: "$85.00 ea" (JCP 2026-11-02; supersedes the 09-10 "Sold at"
 * label). Always the positive unit price, also on a return line.
 */
export function formatEach(value: number | null): string {
  return value === null ? UNKNOWN : `${currencyFormatter.format(Math.abs(value))} ea`
}

/**
 * An order total or line value. Negative (a return) reads as a credit:
 * "−$28,050.00 credit", with a real minus sign (U+2212), never "-$…" and
 * never a bare negative quantity.
 */
export function formatAmount(value: number | null): string {
  if (value === null) return UNKNOWN
  if (value < 0) return `−${currencyFormatter.format(-value)} credit`
  return currencyFormatter.format(value)
}

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })

/**
 * "3 hours ago", "2 days ago" — for the data-freshness banner. Rounds down
 * to the largest whole unit (minutes under an hour, hours under two days,
 * then days).
 */
export function formatRelativeAge(ageMs: number): string {
  const minutes = Math.max(0, Math.floor(ageMs / 60000))
  if (minutes < 60) return relativeFormatter.format(-minutes, 'minute')
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return relativeFormatter.format(-hours, 'hour')
  return relativeFormatter.format(-Math.floor(hours / 24), 'day')
}

/**
 * `value` is a `timestamptz` column (e.g. order_lines.date_created) —
 * already carries real time+offset info, so it parses directly with no
 * synthetic UTC anchor. Null here means missing data, not "not yet set" the
 * way a null required_date does, so it falls back to "Unknown" like the
 * other formatters above, not "TBD".
 */
export function formatTimestamp(value: string | null): string {
  if (value === null) return UNKNOWN
  return dateFormatter.format(new Date(value))
}

/**
 * Identifiers — SKUs, order numbers, invoice numbers, PO numbers — render
 * UPPERCASE always, per the vault's mobile type scale
 * (identifiers-are-monospace): "SKUs, order numbers, invoice numbers and PO
 * numbers render monospaced, UPPERCASE, at Meta or Label size." Named
 * `formatSku` originally, when only item_id needed it; renamed once the rule
 * was stated to cover order numbers too, rather than keeping a SKU-specific
 * name for a general-identifier function. This app's seed data happens to
 * already be uppercase (ZZ-SEED-SKU-001, ZZ-SEED-A-0001), which masks that
 * nothing enforces it — a real P21 identifier may not be. Apply at every
 * identifier display site; pair with `--esl-font-mono` and Meta/Label size
 * in CSS, which this function does not set.
 */
export function formatIdentifier(value: string): string {
  return value.toUpperCase()
}

/**
 * A pure string transform — de-underscore and title-case whatever
 * orders.status holds. NO value map, no branching on which strings are
 * "known": Jeff's real status vocabulary is still an open question, and this
 * must render whatever comes back, including something never seen before,
 * exactly as safely as 'partially_shipped' -> 'Partially Shipped'.
 */
export function formatStatus(value: string | null): string {
  if (value === null) return UNKNOWN
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const asOfFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Denver',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
})

/**
 * "as of Oct 30, 8:15 AM MDT" — a sync time, in Mountain time (ESL's own
 * zone, the same "today" the order views use). Null → "as of unknown".
 */
export function formatAsOf(value: string | null): string {
  if (value === null) return 'as of unknown'
  return `as of ${asOfFormatter.format(new Date(value))}`
}
